import type { CSSProperties } from 'react';
import { useState } from 'react';
import { redirect, useRevalidator } from 'react-router';
import { motion } from 'motion/react';
import { preload } from 'react-dom';

import { cn } from '@april9au/react-common/utils';

import type { Route } from './+types/welcome';
import { metaFor } from '@/cms/meta';
import { topicModuleKey } from '@/cms/topics';
import { getTopics } from '@/cms/topics.server';
import { DevelopedBy } from '@/components/developed-by';
import { MotionLink } from '@/components/motion/motion-ui';
import { pressSpring } from '@/components/motion/tokens';
import { pressProps, stagedChild } from '@/components/motion/variants';
import { useOnboardingBack } from '@/components/onboarding/onboarding-chrome';
import { ONBOARDING_STEPS, OnboardingHeading, StepCounter } from '@/components/onboarding/onboarding-content';
import { WelcomeLanguagePicker } from '@/components/onboarding/welcome-language-picker';
import { buttonVariants } from '@/components/ui/button';
import { Icon } from '@/components/ui/icon';
import { useRootLoaderData } from '@/hooks/use-root-loader-data';
import { formatString } from '@/i18n/catalogue';
import { useStrings } from '@/i18n/use-strings';
import { track } from '@/lib/analytics';
import { guardWelcome } from '@/store/guards';
import { importLegacyOnce } from '@/store/migrate-dexie';
import { useUserStore } from '@/store/user';

export function meta({ matches }: Route.MetaArgs) {
  const siteSettings = matches[0]?.data.siteSettings;

  return siteSettings ? metaFor({ siteSettings, path: '/' }) : [{ title: 'Beach Passport' }];
}

/**
 * The topic UID → `module_key` map the legacy import needs to match old Dexie
 * `topicScore` rows to modules. It is served here because `/` is the first
 * screen a returning learner from the previous version lands on.
 */
export async function loader() {
  const topics = await getTopics();

  return { topicKeys: topics.map((topic) => ({ uid: topic.uid, moduleKey: topicModuleKey(topic) })) };
}

/**
 * Runs on hydration as well as on client navigation, so a returning learner
 * who typed `/` is redirected without ever seeing the pitch twice. There is no
 * `HydrateFallback`: the welcome copy is server-rendered, which keeps the
 * first paint fast and indexable, and the redirect happens a beat later.
 */
export async function clientLoader({ serverLoader }: Route.ClientLoaderArgs) {
  const data = await serverLoader();

  // One-shot import of the previous version's IndexedDB before the guard reads
  // the store, so a learner with old progress is sent straight to their journey.
  await importLegacyOnce(data.topicKeys);

  const guard = guardWelcome(useUserStore.getState().profile);

  if (guard) {
    throw redirect(guard.to);
  }

  return data;
}

clientLoader.hydrate = true as const;

export default function Welcome() {
  const { onboarding } = useStrings();

  const { siteSettings, languages, language } = useRootLoaderData();

  const setLanguage = useUserStore((state) => state.setLanguage);
  const revalidator = useRevalidator();

  // The first screen of the flow: nowhere to go back to.
  useOnboardingBack(null);

  /*
    The page is already rendering in a language, so the picker starts with that
    one ticked. Holding the tapped code locally as well is what keeps the tick
    from flashing: `language` only catches up when the revalidation lands, and
    for those ~100 ms the tap would otherwise look ignored.
  */
  const [picked, setPicked] = useState<string | null>(null);

  const chooseLanguage = (code: string) => {
    setPicked(code);
    // Writes the language cookie, so the revalidation below re-runs the root
    // loader — and this screen's own — against the language just chosen.
    setLanguage(code);
    track('language_selected', { language: code });

    void revalidator.revalidate();
  };

  // The wordmark is the first thing on the screen and the only image in the
  // card, so it is asked for at head time rather than discovered in the body.
  preload(siteSettings.homeLogoUrl, { as: 'image', fetchPriority: 'high' });

  return (
    /*
      The pitch sits in the flow's navy-frosted card rather than directly on the
      photograph. Two things come out of that: white copy has a surface with a
      measured contrast floor instead of whatever the photo happens to be doing
      behind it, and the lifesavers read *through* the card and around its
      edges — visible, which the old flat navy wash never allowed.
    */
    /*
      The `max-height` variants are not a phone/desktop split — they are a
      *short viewport* one, and a 390 × 844 phone is one of them the moment
      Safari's address bar takes its 90 px. The picker costs about 180 px of
      the card, so on a short viewport the hero gives some back rather than
      pushing Start under the fold.
    */
    <div className="flex flex-col items-center gap-3 text-center [@media(max-height:800px)]:gap-2">
      {/*
        Same counter as steps 2 and 3, in the same place: the top of the card,
        label at the start, segments running the full width. The column below
        centres its children, so the counter has to claim the width itself or
        it collapses to its label and the segments vanish.
      */}
      <StepCounter
        step={1}
        total={ONBOARDING_STEPS}
        tone="inverse"
        className="w-full text-start"
        label={formatString(onboarding.step_progress, { step: 1, total: ONBOARDING_STEPS })}
      />

      {/*
        Reserved box with `object-contain`. `site_settings.home_logo` is a
        Prismic image, so its ratio is not knowable here — a fixed box is the
        only thing that can promise the pitch below it will not move when the
        logo lands.

        The box is wider than the bundled portrait logo needs so that a
        seasonal landscape lockup gets the room to be legible instead of being
        squeezed into a portrait slot; `object-contain` means the extra width
        costs the portrait asset nothing. Height is what actually sizes the
        mark, so the short-viewport variant mostly trades height.

        300 px, in the room the old facts panel took: SLSA asked for the
        wordmark and the Qantas line at its foot to read larger.
      */}
      {/* `home_logo_height` replaces the 300 px box. Short screens cap it at
          208 px so Start stays on a phone. */}
      <motion.div
        {...stagedChild}
        className={cn(
          'flex shrink-0 items-center justify-center',
          siteSettings.logoHeights.home === null
            ? 'h-75 w-80 [@media(max-height:800px)]:h-52 [@media(max-height:800px)]:w-64'
            : 'h-(--home-logo) w-full max-w-80 [@media(max-height:800px)]:h-[min(var(--home-logo),13rem)]',
        )}
        style={
          siteSettings.logoHeights.home === null
            ? undefined
            : ({ '--home-logo': `${siteSettings.logoHeights.home}px` } as CSSProperties)
        }
      >
        <img
          src={siteSettings.homeLogoUrl}
          alt={siteSettings.seo.defaultTitle}
          width={666}
          height={1105}
          fetchPriority="high"
          className="max-h-full max-w-full object-contain drop-shadow-[0_8px_24px_rgb(2_20_66_/_0.45)]"
        />
      </motion.div>

      <motion.div {...stagedChild} className="space-y-2">
        <OnboardingHeading size="hero" className="text-balance">
          {onboarding.welcome_title}
        </OnboardingHeading>
        <p className="mx-auto max-w-sm text-sm leading-relaxed text-balance text-white/90 sm:text-base">
          {onboarding.welcome_intro}
        </p>
      </motion.div>

      {/*
        Step 1 of the flow, asked here rather than on `/start` so the very
        first screen a learner who reads no English lands on is one they can
        switch out of. It sits between the two blocks it belongs with: it
        answers the panel above it, and Start below it acts on the answer.
      */}
      <WelcomeLanguagePicker
        languages={languages}
        selected={picked ?? language}
        onSelect={chooseLanguage}
        label={onboarding.welcome_language_label}
      />

      <motion.div {...stagedChild} className="w-full space-y-2">
        <MotionLink
          to="/start"
          {...pressProps}
          transition={pressSpring}
          className={cn(buttonVariants({ variant: 'brand-yellow', size: 'xl' }), 'bp-tappable h-14 text-lg')}
        >
          {onboarding.welcome_start}
          <Icon name="passport/arrow-right" className="size-5 rtl:rotate-180" />
        </MotionLink>

        <p className="text-xs leading-relaxed text-white/80 sm:text-sm">{onboarding.welcome_trust}</p>

        {/* Directly under the trust line, and quieter than it: who built this
            is the last thing on the screen a learner needs, but the only place
            in the flow the credit can honestly live — the question screens
            belong to the questions. */}
        <DevelopedBy className="text-white" />
      </motion.div>
    </div>
  );
}
